import React, { type ReactElement } from 'react';
import { capitalize } from 'lodash';
import { useParams } from 'react-router-dom';
import Card from 'atom-components/card';
import Loading from 'atom-components/loading';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import MainLayout from 'layouts/main';
import { organisationUsersWithMarkerRoleParams } from 'pages/marking/helpers/request-params.helpers';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import { useGroupWithNoDefaults } from 'services/assist-api/entities/group/groups';
import type { GroupWithGroupUsers } from 'services/assist-api/entities/group/types';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { isInitialLoad } from 'utils/check-query-status';
import Breadcrumbs from './breadcrumbs';
import MarkingGroupForm from './marking-group-form';
import { PageAction } from './types';

export default function MarkingGroupPage(): ReactElement {
  const { groupId } = useParams(); // Extracted from the slug
  const pageAction: PageAction = groupId ? PageAction.Edit : PageAction.New;

  // Get the saved group if a group ID is specified. New groups haven't been saved yet, so this
  // query should not prevent the form from loading.
  const markingGroupQuery = useGroupWithNoDefaults<GroupWithGroupUsers>(groupId, {
    groups: [
      'id', // Adds the ID field to all subentities as well
      'group-users',
      'groupsusers-user'
    ],
    include: 'users'
  });

  // Get organisation users who have the marker role:
  const currentOrganisation = useAuthCustomer();
  const markerOrganisationUsersQuery = useCustomerUsers(
    organisationUsersWithMarkerRoleParams(currentOrganisation?.id),
    { enabled: !!currentOrganisation?.id }
  );

  // Get organisation users who do not have the marker role but were previously added to the group.
  // This is required to view and remove users from the group who no longer have the Marker role.
  const markerOrganisationUserIds =
    markerOrganisationUsersQuery.data?.json.map((ou) => ou.id) || [];
  const nonMarkerOrganisationUserIds =
    markingGroupQuery.data?.json.users
      ?.filter((gu) => !markerOrganisationUserIds.includes(gu.user.id))
      .map((gu) => gu.user.id) || [];
  const nonMarkerOrganisationUsersQuery = useCustomerUsers(
    {
      filters: {
        id: nonMarkerOrganisationUserIds,
        customer: currentOrganisation?.id
      },
      groups: ['id']
    },
    {
      enabled:
        !!currentOrganisation?.id &&
        // Both a group (when creating) and users subobject (when editing) may not be returned, so
        // only enable the query when both of these have data and there's at least one group user:
        nonMarkerOrganisationUserIds.length > 0 &&
        markerOrganisationUsersQuery.isSuccess
    }
  );

  const isLoading =
    !currentOrganisation ||
    isInitialLoad(markingGroupQuery, markerOrganisationUsersQuery, nonMarkerOrganisationUsersQuery);
  const isError =
    markingGroupQuery.isError ||
    markerOrganisationUsersQuery.isError ||
    nonMarkerOrganisationUsersQuery.isError;

  return (
    <MainLayout title="Marking" breadcrumbs={<Breadcrumbs pageAction={pageAction} />}>
      {isLoading && <Loading />}
      {!isLoading && isError && (
        <LoadingErrorAlert
          queries={[
            markingGroupQuery,
            markerOrganisationUsersQuery,
            nonMarkerOrganisationUsersQuery
          ]}
        />
      )}
      {!isLoading && !isError && (
        <Card>
          <Card.Header>
            <Card.Heading level={3}>{capitalize(pageAction)} Marking Group</Card.Heading>
          </Card.Header>
          <Card.Body>
            <MarkingGroupForm
              currentOrganisationId={currentOrganisation.id}
              markerOrganisationUsers={markerOrganisationUsersQuery.data?.json || []}
              nonMarkerOrganisationUsers={nonMarkerOrganisationUsersQuery.data?.json || []}
              markingGroup={markingGroupQuery.data?.json}
            />
          </Card.Body>
        </Card>
      )}
    </MainLayout>
  );
}
